import { localize } from "@/i18n/translate";
import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { errorCode } from "@/lib/api";
import { useAuth } from "../AuthContext";
import { usePortal } from "../i18n";
import { AuthLayout } from "../AuthLayout";
import { ErrorNote, FormField, btnPrimary, inputCls } from "../ui";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export default function Login() {
  const {
    user,
    login
  } = useAuth();
  const {
    p,
    errText
  } = usePortal();
  const navigate = useNavigate();
  const location = useLocation();
  const [v, setV] = useState({
    email: "",
    password: ""
  });
  const [errors, setErrors] = useState({});
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/kundenbereich"} replace />;
  const submit = async e => {
    e.preventDefault();
    const errs = {};
    if (!EMAIL_RE.test(v.email.trim())) errs.email = p.auth.err.email;
    if (!v.password) errs.password = p.auth.err.passwordEmpty;
    setErrors(errs);
    if (Object.keys(errs).length) return document.getElementById(Object.keys(errs)[0])?.focus();
    setBusy(true);
    setErr(null);
    try {
      const u = await login(v.email.trim(), v.password);
      const from = location.state?.from;
      navigate(u.role === "admin" ? from?.startsWith("/admin") ? from : "/admin" : from?.startsWith("/kundenbereich") ? from : "/kundenbereich", {
        replace: true
      });
    } catch (ex) {
      setErr(errText(errorCode(ex)));
      setBusy(false);
    }
  };
  const aria = k => ({
    "aria-invalid": errors[k] ? true : undefined,
    "aria-describedby": errors[k] ? `${k}-error` : undefined
  });
  return <AuthLayout>
      <p className="eyebrow">{localize(p.area)}</p>
      <h1 className="mt-4 font-display text-4xl font-extrabold tracking-[-0.03em] sm:text-5xl">{localize(p.auth.loginTitle)}</h1>
      <p className="mt-4 leading-relaxed text-mute">{localize(p.auth.loginIntro)}</p>
      <form onSubmit={submit} noValidate className="mt-10 space-y-6" data-testid="login-form">
        <ErrorNote testId="login-error">{localize(err)}</ErrorNote>
        <FormField id="email" label={p.auth.email} error={errors.email}>
          <input id="email" type="email" autoComplete="email" value={v.email} onChange={e => {
          setV({
            ...v,
            email: e.target.value
          });
          setErrors({
            ...errors,
            email: undefined
          });
        }} className={inputCls(errors.email)} data-testid="login-email-input" {...aria("email")} />
        </FormField>
        <FormField id="password" label={p.auth.password} error={errors.password}>
          <input id="password" type="password" autoComplete="current-password" value={v.password} onChange={e => {
          setV({
            ...v,
            password: e.target.value
          });
          setErrors({
            ...errors,
            password: undefined
          });
        }} className={inputCls(errors.password)} data-testid="login-password-input" {...aria("password")} />
        </FormField>
        <button type="submit" disabled={busy} className={`${btnPrimary} w-full`} data-testid="login-submit-btn">
          {localize(busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />)} {localize(p.auth.submitLogin)}
        </button>
      </form>
      <p className="mt-8 text-sm text-mute">
        {localize(p.auth.noAccount)}{localize(" ")}
        <Link to="/kundenbereich/registrieren" className="font-medium text-ink underline underline-offset-4 hover:text-signal" data-testid="login-to-register">{localize(p.auth.toRegister)}</Link>
      </p>
    </AuthLayout>;
}
